import React, { useEffect, useState } from 'react';
import {
  Box, Card, Typography, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Alert, CircularProgress, IconButton, Switch,
  Autocomplete
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon, Group as GroupIcon } from '@mui/icons-material';
import { DeliveryGroup, deliveryGroupsAPI, User, usersAPI } from '../../services/api';
import { toPersianNumber } from '../../utils/persianNumber';

const DeliveryGroups: React.FC = () => {
  const [groups, setGroups] = useState<DeliveryGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [openMembersModal, setOpenMembersModal] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<DeliveryGroup | null>(null);
  const [formData, setFormData] = useState({ title: '', master_card_number: '', is_active: true });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Members state
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [selectedUserToAdd, setSelectedUserToAdd] = useState<User | null>(null);

  useEffect(() => {
    loadGroups();
    loadAllUsers();
  }, []);

  const loadGroups = async () => {
    try {
      setLoading(true);
      const data = await deliveryGroupsAPI.getGroups();
      setGroups(data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'خطا در بارگذاری گروه‌ها');
    } finally {
      setLoading(false);
    }
  };

  const loadAllUsers = async () => {
    try {
      // Load all personnel
      const data = await usersAPI.getUsers('staff');
      setAllUsers(data);
    } catch (err) {
      console.error('Failed to load users');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    try {
      if (selectedGroup) {
        await deliveryGroupsAPI.updateGroup(selectedGroup.id, formData);
        setSuccess('گروه با موفقیت ویرایش شد');
      } else {
        await deliveryGroupsAPI.createGroup(formData);
        setSuccess('گروه با موفقیت اضافه شد');
      }
      setOpenModal(false);
      loadGroups();
    } catch (err: any) {
      setError(err.response?.data?.error || 'خطا در ذخیره اطلاعات');
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('آیا از حذف این گروه مطمئن هستید؟')) {
      try {
        await deliveryGroupsAPI.deleteGroup(id);
        setSuccess('گروه با موفقیت حذف شد');
        loadGroups();
      } catch (err: any) {
        setError(err.response?.data?.error || 'خطا در حذف گروه');
      }
    }
  };

  const handleOpenMembers = async (group: DeliveryGroup) => {
    try {
      const data = await deliveryGroupsAPI.getGroupById(group.id);
      setSelectedGroup(data);
      setOpenMembersModal(true);
    } catch (err) {
      setError('خطا در بارگذاری اعضای گروه');
    }
  };

  const handleAddMember = async () => {
    if (!selectedGroup || !selectedUserToAdd) return;
    try {
      await deliveryGroupsAPI.addMember(selectedGroup.id, selectedUserToAdd.id);
      setSuccess('عضو با موفقیت اضافه شد');
      setSelectedUserToAdd(null);
      // Reload members
      handleOpenMembers(selectedGroup);
      loadGroups(); // To update member count
    } catch (err: any) {
      setError(err.response?.data?.error || 'خطا در افزودن عضو');
    }
  };

  const handleRemoveMember = async (userId: string) => {
    if (!selectedGroup) return;
    try {
      await deliveryGroupsAPI.removeMember(selectedGroup.id, userId);
      setSuccess('عضو با موفقیت حذف شد');
      handleOpenMembers(selectedGroup);
      loadGroups(); // To update member count
    } catch (err: any) {
      setError(err.response?.data?.error || 'خطا در حذف عضو');
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4" sx={{ mb: 4, fontFamily: 'Vazir, sans-serif', fontWeight: 'bold' }}>
          مدیریت تحویل گروهی
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            setSelectedGroup(null);
            setFormData({ title: '', master_card_number: '', is_active: true });
            setOpenModal(true);
            setError('');
            setSuccess('');
          }}
          sx={{ fontFamily: 'Vazir, sans-serif', borderRadius: '12px', height: '48px' }}
        >
          افزودن گروه جدید
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2, fontFamily: 'Vazir, sans-serif' }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2, fontFamily: 'Vazir, sans-serif' }}>{success}</Alert>}

      <Card sx={{ borderRadius: '16px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 'bold' }}>ردیف</TableCell>
                  <TableCell sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 'bold' }}>عنوان گروه</TableCell>
                  <TableCell sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 'bold' }}>کارت تحویل‌گیرنده</TableCell>
                  <TableCell sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 'bold' }}>تعداد اعضا</TableCell>
                  <TableCell sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 'bold' }}>وضعیت</TableCell>
                  <TableCell sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 'bold', textAlign: 'center' }}>عملیات</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {groups.map((group, index) => (
                  <TableRow key={group.id}>
                    <TableCell sx={{ fontFamily: 'Vazir, sans-serif' }}>{toPersianNumber(index + 1)}</TableCell>
                    <TableCell sx={{ fontFamily: 'Vazir, sans-serif' }}>{group.title}</TableCell>
                    <TableCell sx={{ fontFamily: 'Vazir, sans-serif' }}>{toPersianNumber(group.master_card_number)}</TableCell>
                    <TableCell sx={{ fontFamily: 'Vazir, sans-serif' }}>{toPersianNumber(group.member_count || 0)} نفر</TableCell>
                    <TableCell sx={{ fontFamily: 'Vazir, sans-serif' }}>
                      {group.is_active ? <Typography color="success.main">فعال</Typography> : <Typography color="error.main">غیرفعال</Typography>}
                    </TableCell>
                    <TableCell align="center">
                      <IconButton color="info" onClick={() => handleOpenMembers(group)} title="اعضای گروه">
                        <GroupIcon />
                      </IconButton>
                      <IconButton
                        color="primary"
                        onClick={() => {
                          setSelectedGroup(group);
                          setFormData({ title: group.title, master_card_number: group.master_card_number, is_active: group.is_active });
                          setOpenModal(true);
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton color="error" onClick={() => handleDelete(group.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {groups.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ fontFamily: 'Vazir, sans-serif', py: 3 }}>
                      گروهی یافت نشد
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      {/* Group Modal */}
      <Dialog open={openModal} onClose={() => setOpenModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontFamily: 'Vazir, sans-serif' }}>
          {selectedGroup ? 'ویرایش گروه تحویل' : 'افزودن گروه تحویل جدید'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <TextField
              fullWidth
              label="عنوان گروه"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              margin="normal"
              required
              InputProps={{ sx: { fontFamily: 'Vazir, sans-serif', height: '48px' } }}
              InputLabelProps={{ sx: { fontFamily: 'Vazir, sans-serif' } }}
            />
            <TextField
              fullWidth
              label="شماره کارت تحویل‌گیرنده"
              value={formData.master_card_number}
              onChange={(e) => setFormData({ ...formData, master_card_number: e.target.value })}
              margin="normal"
              required
              InputProps={{ sx: { fontFamily: 'Vazir, sans-serif', height: '48px' } }}
              InputLabelProps={{ sx: { fontFamily: 'Vazir, sans-serif' } }}
            />
            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
              <Typography sx={{ fontFamily: 'Vazir, sans-serif', mr: 2 }}>وضعیت کارت:</Typography>
              <Switch
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button onClick={() => setOpenModal(false)} sx={{ fontFamily: 'Vazir, sans-serif' }}>
              انصراف
            </Button>
            <Button type="submit" variant="contained" sx={{ fontFamily: 'Vazir, sans-serif', borderRadius: '12px' }}>
              ذخیره
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Members Modal */}
      <Dialog open={openMembersModal} onClose={() => setOpenMembersModal(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 'bold' }}>
          مدیریت اعضای گروه: {selectedGroup?.title}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', gap: 2, mb: 3, mt: 1 }}>
            <Autocomplete
              options={allUsers.filter(u => !selectedGroup?.members?.find(m => m.id === u.id))}
              getOptionLabel={(option) => `${option.firstName} ${option.lastName} (${option.personnelCode || '-'})`}
              value={selectedUserToAdd}
              onChange={(_, newValue) => setSelectedUserToAdd(newValue)}
              fullWidth
              renderInput={(params) => (
                <TextField 
                  {...params} 
                  label="جستجوی پرسنل" 
                  InputProps={{ ...params.InputProps, style: { fontFamily: 'Vazir, sans-serif', height: '48px' } }}
                  InputLabelProps={{ sx: { fontFamily: 'Vazir, sans-serif' } }}
                />
              )}
            />
            <Button
              variant="contained"
              onClick={handleAddMember}
              disabled={!selectedUserToAdd}
              sx={{ fontFamily: 'Vazir, sans-serif', borderRadius: '12px', minWidth: '120px', height: '48px' }}
            >
              افزودن
            </Button>
          </Box>
          
          <TableContainer component={Card} sx={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 'bold' }}>ردیف</TableCell>
                  <TableCell sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 'bold' }}>نام و نام خانوادگی</TableCell>
                  <TableCell sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 'bold' }}>کد پرسنلی</TableCell>
                  <TableCell sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 'bold', textAlign: 'center' }}>عملیات</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedGroup?.members?.map((member, idx) => (
                  <TableRow key={member.id}>
                    <TableCell sx={{ fontFamily: 'Vazir, sans-serif' }}>{toPersianNumber(idx + 1)}</TableCell>
                    <TableCell sx={{ fontFamily: 'Vazir, sans-serif' }}>{member.firstName} {member.lastName}</TableCell>
                    <TableCell sx={{ fontFamily: 'Vazir, sans-serif' }}>{toPersianNumber(member.personnelCode || '-')}</TableCell>
                    <TableCell align="center">
                      <IconButton color="error" size="small" onClick={() => handleRemoveMember(member.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {(!selectedGroup?.members || selectedGroup.members.length === 0) && (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ fontFamily: 'Vazir, sans-serif', py: 2 }}>
                      هیچ عضوی در این گروه وجود ندارد
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setOpenMembersModal(false)} variant="contained" color="inherit" sx={{ fontFamily: 'Vazir, sans-serif', borderRadius: '12px' }}>
            بستن
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DeliveryGroups;
